import { useEffect, useMemo, useState } from "react";
import {
  ShieldCheck, User, Building2, Send, ClipboardList, Users as UsersIcon,
  ShieldAlert, Settings, Database, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ADMIN_ROLE_LABEL } from "@/components/AdminShell";

// Defensive text coercion: prevents crashes when audit rows accidentally
// store objects/arrays in string-typed fields.
const safeText = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return ""; }
};

const MODULE_FILTERS = [
  { k: "", label: "All modules" },
  { k: "garage", label: "Garage" },
  { k: "user", label: "User" },
  { k: "enquiry", label: "Enquiry" },
  { k: "admin_members", label: "Admin members" },
];

const ACTION_TONE = {
  view: "bg-[#457b9d]/10 text-[#457b9d]",
  create: "bg-[#12B76A]/10 text-[#12B76A]",
  update: "bg-[#F79009]/10 text-[#F79009]",
  suspend: "bg-[#e63946]/10 text-[#e63946]",
  reactivate: "bg-[#12B76A]/10 text-[#12B76A]",
  delete: "bg-[#e63946]/10 text-[#e63946]",
};

const MODULE_ICON = {
  garage: Building2,
  user: User,
  enquiry: Send,
  job_card: ClipboardList,
  admin_members: ShieldAlert,
  settings: Settings,
  master_data: Database,
};

export default function AdminAuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleF, setModuleF] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (moduleF) params.module_f = moduleF;
    api.get("/admin/audit-log", { params })
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load();   }, [moduleF]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) =>
      (r.actor_email || "").toLowerCase().includes(s) ||
      (r.target_name || "").toLowerCase().includes(s) ||
      (r.detail || "").toLowerCase().includes(s) ||
      (r.action || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  return (
    <div className="space-y-5" data-testid="admin-audit-log-root">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Governance</div>
        <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
          <ShieldCheck size={22} className="text-[#457b9d]" /> Audit Log
        </h1>
        <p className="text-[13px] text-[#667085] mt-0.5">
          Append-only trail of every high-impact admin action. Most recent {filtered.length} of {rows.length} entries.
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 bg-white rounded-md border border-[#E4E7EC] p-2">
        <select
          value={moduleF}
          onChange={(e) => setModuleF(e.target.value)}
          data-testid="admin-audit-module-filter"
          className="h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
        >
          {MODULE_FILTERS.map((m) => (
            <option key={m.k} value={m.k}>{m.label}</option>
          ))}
        </select>
        <Input
          data-testid="admin-audit-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by actor, target or action…"
          className="flex-1 min-w-[220px] h-9 rounded-md text-[13px]"
        />
        <button
          onClick={load}
          data-testid="admin-audit-refresh"
          className="h-9 px-3 rounded-md border border-[#E4E7EC] bg-white text-[13px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-audit-log-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">When</th>
              <th className="text-left px-4 py-3 font-semibold">Admin</th>
              <th className="text-left px-4 py-3 font-semibold">Action</th>
              <th className="text-left px-4 py-3 font-semibold">Target</th>
              <th className="text-left px-4 py-3 font-semibold">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {loading && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-[13px] text-[#667085]">Loading audit trail…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-[13px] text-[#667085]">No audit events yet.</td></tr>
            )}
            {filtered.map((r, i) => {
              const Icon = MODULE_ICON[safeText(r.module)] || ShieldCheck;
              const tone = ACTION_TONE[safeText(r.action)] || "bg-[#66708515] text-[#667085]";
              const detailText = safeText(r.detail);
              const targetText = safeText(r.target_name) || safeText(r.target_label) || safeText(r.target_id) || "—";
              return (
                <tr key={r.id || i} className="hover:bg-[#F7F9FC]" data-testid={`admin-audit-row-${i}`}>
                  <td className="px-4 py-3 text-[11px] font-mono text-[#667085] whitespace-nowrap">{fmtDateTime(r.at)}</td>
                  <td className="px-4 py-3">
                    <div className="text-[13px] text-[#1d3557] font-semibold">{safeText(r.actor_email) || "—"}</div>
                    <div className="text-[11px] text-[#457b9d] uppercase tracking-wider font-semibold">
                      {ADMIN_ROLE_LABEL[safeText(r.actor_admin_role)] || safeText(r.actor_admin_role) || ""}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${tone}`}>
                      {safeText(r.action)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[12px] text-[#1d3557] font-semibold flex items-center gap-1.5">
                      <Icon size={12} className="text-[#457b9d]" /> {safeText(r.module).replace(/_/g, " ")}
                    </div>
                    <div className="text-[11px] text-[#667085] font-mono truncate max-w-[240px]">
                      {targetText}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-[#667085] max-w-md truncate" title={detailText}>
                    {detailText || <span className="text-[#a8dadc]">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
